import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as authApi from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'

export function MfaSetup() {
  const { user, loading, mfaEnabled, requireMfa, setUser, logout } = useAuth()
  const navigate = useNavigate()
  const [setup, setSetup] = useState(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loadingSetup, setLoadingSetup] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!user || mfaEnabled || !requireMfa) return
      setLoadingSetup(true)
      setError('')
      try {
        const data = await authApi.setupMfa()
        if (!cancelled) setSetup(data)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not start 2FA setup.',
          )
        }
      } finally {
        if (!cancelled) setLoadingSetup(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [user, mfaEnabled, requireMfa])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200">
        <p className="text-brand-muted">Loading…</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!requireMfa || mfaEnabled) {
    return <Navigate to="/" replace />
  }

  async function handleConfirm(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const updated = await authApi.confirmMfa(code.trim())
      setUser(updated)
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Invalid code. Try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200 p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white/90 p-8 shadow-sm backdrop-blur">
        <p className="font-display text-3xl text-brand">Set up 2FA</p>
        <p className="mt-2 text-sm text-brand-muted">
          Scan the QR code with Google Authenticator (or similar), then enter the
          6-digit code to finish. Two-factor authentication is required for all
          accounts.
        </p>

        {loadingSetup && <p className="mt-6 text-sm text-brand-muted">Preparing…</p>}

        {setup && (
          <div className="mt-6 space-y-4">
            <img
              src={setup.qr_code_data_url}
              alt="Authenticator QR code"
              className="mx-auto h-48 w-48 rounded-md border border-slate-200 bg-white p-2"
            />
            <p className="text-center text-xs text-brand-muted">
              Or enter this key manually:{' '}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-brand">
                {setup.secret}
              </code>
            </p>

            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-brand">
                  Authenticator code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={submitting}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 tracking-widest text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-700" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? 'Confirming…' : 'Confirm and continue'}
              </button>
            </form>
          </div>
        )}

        {error && !setup && (
          <p className="mt-4 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={() => logout()}
          className="mt-6 text-sm text-brand-muted underline"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
